import React, { useState, useEffect } from 'react';
import { ThemedButton } from '../ThemedButton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Users } from 'lucide-react';
import { useWizard } from '../../contexts/WizardContext';

const AVAILABLE_SPORTS = [
  'Baseball',
  'Basketball',
  'Cheerleading',
  'Cross Country',
  'Field Hockey',
  'Flag Football',
  'Golf',
  'Gymnastics',
  'Ice Hockey',
  'Lacrosse',
  'Soccer',
  'Softball',
  'Swimming',
  'Tackle Football',
  'Tennis',
  'Track & Field',
  'Volleyball',
  'Wrestling',
  'Other'
];

const SportsParticipants = () => {
  const { formData, updateFormData, nextStep } = useWizard();
  const [selectedSports, setSelectedSports] = useState(
    formData.sports || []
  );
  const [otherSportName, setOtherSportName] = useState('');
  const [numCoaches, setNumCoaches] = useState(formData.num_coaches || '');
  const [ageBreakdown, setAgeBreakdown] = useState({
    age_12_and_under: formData.age_12_and_under || '',
    age_13_to_15: formData.age_13_to_15 || '',
    age_16_to_18: formData.age_16_to_18 || ''
  });
  const [error, setError] = useState('');

  // Calculate total participants
  const totalParticipants = selectedSports.reduce((sum, s) => sum + (parseInt(s.participants) || 0), 0);

  const handleSportToggle = (sportName) => {
    const existing = selectedSports.find(s => s.sport === sportName);
    if (existing) {
      // Remove sport
      setSelectedSports(selectedSports.filter(s => s.sport !== sportName));
    } else {
      // Add sport with 0 participants
      setSelectedSports([...selectedSports, { sport: sportName, participants: '' }]);
    }
    setError('');
  };

  const handleParticipantChange = (sportName, value) => {
    setSelectedSports(selectedSports.map(s => 
      s.sport === sportName ? { ...s, participants: value } : s
    ));
    setError('');
  };

  const handleAddOtherSport = () => {
    if (otherSportName.trim()) {
      const sportName = otherSportName.trim();
      if (!selectedSports.find(s => s.sport.toLowerCase() === sportName.toLowerCase())) {
        setSelectedSports([...selectedSports, { sport: sportName, participants: '' }]);
        setOtherSportName('');
      }
    }
  };

  const handleRemoveSport = (sportName) => {
    setSelectedSports(selectedSports.filter(s => s.sport !== sportName));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (selectedSports.length === 0) {
      setError('Please select at least one sport');
      return;
    }

    const hasInvalidParticipants = selectedSports.some(s => !s.participants || parseInt(s.participants) < 1);
    if (hasInvalidParticipants) {
      setError('Please enter participant count for all selected sports');
      return;
    }

    if (!numCoaches || parseInt(numCoaches) < 0) {
      setError('Please enter the number of coaches/instructors');
      return;
    }

    // Validate age breakdown
    const totalAgeBreakdown = (parseInt(ageBreakdown.age_12_and_under) || 0) + 
                              (parseInt(ageBreakdown.age_13_to_15) || 0) + 
                              (parseInt(ageBreakdown.age_16_to_18) || 0);
    
    if (totalAgeBreakdown === 0) {
      setError('Please enter participant age breakdown');
      return;
    }

    // Format sports data with parsed integers
    const sportsData = selectedSports.map(s => ({
      sport: s.sport,
      participants: parseInt(s.participants)
    }));

    updateFormData({ 
      sports: sportsData,
      num_participants_under_19: totalParticipants,
      num_coaches: parseInt(numCoaches),
      age_12_and_under: parseInt(ageBreakdown.age_12_and_under) || 0,
      age_13_to_15: parseInt(ageBreakdown.age_13_to_15) || 0,
      age_16_to_18: parseInt(ageBreakdown.age_16_to_18) || 0
    });
    nextStep();
  };

  const isSportSelected = (sportName) => {
    return selectedSports.some(s => s.sport === sportName);
  };

  const getParticipantValue = (sportName) => {
    const sport = selectedSports.find(s => s.sport === sportName);
    return sport ? sport.participants : '';
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <Label className="text-base font-semibold">Select Sports Offered by Your Organization *</Label>
        <p className="text-sm text-slate-600 mt-1 mb-4">
          Choose all sports your organization offers and enter the estimated number of participants (under age 19) for each.
        </p>

        {/* Sports Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
          {AVAILABLE_SPORTS.filter(s => s !== 'Other').map((sport) => (
            <label
              key={sport}
              htmlFor={`sport-${sport.toLowerCase().replace(/\s+/g, '-')}`}
              className={`border rounded-lg p-3 cursor-pointer transition-all block ${
                isSportSelected(sport) 
                  ? 'border-purple-400 bg-purple-50 ring-2 ring-purple-200' 
                  : 'border-slate-200 hover:border-purple-300 hover:bg-purple-50/50'
              }`}
            >
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`sport-${sport.toLowerCase().replace(/\s+/g, '-')}`}
                  checked={isSportSelected(sport)}
                  data-testid={`sport-checkbox-${sport.toLowerCase().replace(/\s+/g, '-')}`}
                  onCheckedChange={() => handleSportToggle(sport)}
                />
                <span className="text-sm font-medium text-slate-700">{sport}</span>
              </div>
            </label>
          ))}
        </div>

        {/* Add Custom Sport */}
        <div className="flex gap-2 mb-6">
          <Input
            placeholder="Add another sport..."
            value={otherSportName}
            onChange={(e) => setOtherSportName(e.target.value)}
            data-testid="other-sport-input"
            className="flex-1"
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddOtherSport())}
          />
          <ThemedButton
            type="button"
            variant="outline"
            onClick={handleAddOtherSport}
            data-testid="add-sport-btn"
            disabled={!otherSportName.trim()}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add
          </ThemedButton>
        </div>

        {/* Selected Sports with Participant Counts */}
        {selectedSports.length > 0 && (
          <div className="space-y-3">
            <Label className="text-sm font-semibold text-slate-700">
              Enter Participants for Selected Sports
            </Label>
            <div className="space-y-2">
              {selectedSports.map((sportData) => (
                <div 
                  key={sportData.sport} 
                  className="flex items-center gap-3 bg-[#EEF0FF] border border-[#7B6FE8]/20 rounded-lg p-3"
                >
                  <div className="flex-1">
                    <span className="font-medium text-slate-800">{sportData.sport}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-slate-500" />
                    <Input
                      type="number"
                      min="1"
                      placeholder="# participants"
                      value={sportData.participants}
                      onChange={(e) => handleParticipantChange(sportData.sport, e.target.value)}
                      data-testid={`participants-${sportData.sport.toLowerCase().replace(/\s+/g, '-')}`}
                      className="w-32 h-9"
                    />
                  </div>
                  <ThemedButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveSport(sportData.sport)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </ThemedButton>
                </div>
              ))}
            </div>

            {/* Total Summary */}
            <div className="flex items-center justify-between bg-slate-100 rounded-lg p-4 mt-4">
              <span className="font-semibold text-slate-700">Total Participants</span>
              <Badge className="bg-[#7B6FE8] text-white text-lg px-4 py-1">
                {totalParticipants.toLocaleString()}
              </Badge>
            </div>
          </div>
        )}

        {/* Number of Coaches/Instructors */}
        <div className="mt-6">
          <Label htmlFor="num_coaches" className="text-base font-semibold">Number of Coaches / Instructors *</Label>
          <p className="text-sm text-slate-600 mt-1 mb-3">
            Include all coaches, instructors, trainers, and volunteers who work directly with participants.
          </p>
          <Input
            id="num_coaches"
            type="number"
            min="0"
            placeholder="Enter total number of coaches/instructors"
            value={numCoaches}
            onChange={(e) => {
              setNumCoaches(e.target.value);
              setError('');
            }}
            data-testid="num-coaches-input"
            className="h-12"
          />
        </div>

        {/* Age Breakdown */}
        <div className="mt-6 border border-slate-200 rounded-xl p-5">
          <Label className="text-base font-semibold">Participant Age Breakdown *</Label>
          <p className="text-sm text-slate-600 mt-1 mb-4">
            Enter the number of participants in each age group.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="age_12_and_under" className="text-sm font-medium text-slate-700">12 and Under</Label>
              <Input
                id="age_12_and_under"
                type="number"
                min="0"
                placeholder="0"
                value={ageBreakdown.age_12_and_under}
                onChange={(e) => {
                  setAgeBreakdown({ ...ageBreakdown, age_12_and_under: e.target.value });
                  setError('');
                }}
                data-testid="age-12-under-input"
                className="h-11 mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="age_13_to_15" className="text-sm font-medium text-slate-700">13 - 15</Label>
              <Input
                id="age_13_to_15"
                type="number"
                min="0"
                placeholder="0"
                value={ageBreakdown.age_13_to_15}
                onChange={(e) => {
                  setAgeBreakdown({ ...ageBreakdown, age_13_to_15: e.target.value });
                  setError('');
                }}
                data-testid="age-13-15-input"
                className="h-11 mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="age_16_to_18" className="text-sm font-medium text-slate-700">16 - 18</Label>
              <Input
                id="age_16_to_18"
                type="number"
                min="0"
                placeholder="0"
                value={ageBreakdown.age_16_to_18}
                onChange={(e) => {
                  setAgeBreakdown({ ...ageBreakdown, age_16_to_18: e.target.value });
                  setError('');
                }}
                data-testid="age-16-18-input"
                className="h-11 mt-1.5"
              />
            </div>
          </div>

          {/* Age Breakdown Total */}
          <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3 mt-4">
            <span className="text-sm font-medium text-slate-600">Total from Age Breakdown</span>
            <Badge variant="outline" className="text-sm px-3 py-1">
              {((parseInt(ageBreakdown.age_12_and_under) || 0) + 
                (parseInt(ageBreakdown.age_13_to_15) || 0) + 
                (parseInt(ageBreakdown.age_16_to_18) || 0)).toLocaleString()}
            </Badge>
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-600 mt-3">{error}</p>
        )}
      </div>

      <ThemedButton 
        type="submit"
        data-testid="sports-participants-next-btn"
        variant="wizard" size="full"
      >
        Next: Choose Your Start Date
      </ThemedButton>
    </form>
  );
};

export default SportsParticipants;
